/* eslint-disable @next/next/no-img-element */
import React from 'react';
import classNames from 'classnames';
import { getFocalPoint } from '../../../utils/images';
import { CarouselItems } from '../HeroCarousel';
import styles from './HeroImage.module.scss';
import { getImageProps } from 'next/image';

const HERO_IMAGE_SIZES = '(max-width: 940px) 100vw, 940px';

export type HeroImageProps = {
  alt?: string;
  fetchPriority?: FetchPriorityType;
  loading?: LoadingType;
  className?: string;
} & Pick<CarouselItems, 'croppedLogoUrl480' | 'croppedLogoUrl600' | 'croppedLogoUrl940' | 'croppedLogoUrl1880'>;

export type LoadingType = 'eager' | 'lazy';

export enum FetchPriorityType {
  high = 'high',
  low = 'low',
}

export const HeroImage = ({
  className,
  alt,
  croppedLogoUrl480,
  croppedLogoUrl600,
  croppedLogoUrl940,
  croppedLogoUrl1880,
  fetchPriority,
  loading,
}: HeroImageProps) => {
  const bestAvailableUrl =
    [croppedLogoUrl1880, croppedLogoUrl940, croppedLogoUrl600, croppedLogoUrl480].find(Boolean) ?? '';

  if (!bestAvailableUrl) {
    return null;
  }

  const focalPoint = getFocalPoint(bestAvailableUrl);
  const imageClasses = classNames(styles.image, className);
  const resolvedLoading = loading || (fetchPriority === FetchPriorityType.high ? 'eager' : 'lazy');

  const {
    props: { srcSet, sizes, style: imageStyle, ...imgProps },
  } = getImageProps({
    alt: alt || 'Hero image',
    sizes: HERO_IMAGE_SIZES,
    width: 1880,
    height: 940,
    src: bestAvailableUrl,
  });

  const imgStyle = {
    ...imageStyle,
    ...(focalPoint ? { objectPosition: `${focalPoint.x * 100}% ${focalPoint.y * 100}%` } : {}),
  };

  return (
    <picture data-testid="hero-image">
      <source srcSet={srcSet} sizes={sizes} />
      <img
        {...imgProps}
        className={imageClasses}
        alt={alt || 'Hero image'}
        loading={resolvedLoading}
        fetchPriority={fetchPriority}
        style={imgStyle}
        data-testid="hero-img"
      />
    </picture>
  );
};
